<template>
  <div class="grid grid--wide bookslist bookslist--myhome">
    <div v-if="search" class="xs-12">
      <div class="bookslist__filters">
        <InputText
          id="autosearch"
          v-model="searchText"
          class="dea-input--text"
          icon="ricerca"
          :icon-left="true"
          placeholder="Ricerca prodotto o materia"
        />
        <InputSelect id="books-order" v-model="sortValue" placeholder="Ordina per" name="books-order">
          <option
            v-for="(data, index) in selectData"
            :key="index"
            :value="data.value"
            :selected="data.value === sortValue"
          >
            {{ data.label }}
          </option>
        </InputSelect>
      </div>
    </div>
    <div v-for="(item, key) in filteredListItems" :key="`${key}-${item.time_id}`" :class="columns">
      <!-- !child ? <something> : null is used to change the behavior when this is used for children books -->
      <CardProduct
        :link="!child ? item.link : null"
        class
        type="book"
        :image="item.cover"
        :title="item.title"
        :subtitle="item.subtitle"
        :school-target="item.school_target"
        :author="item.authors"
        :editor="item.editor"
        :badge-type="item.badgeType"
        :activation-date="item.start_date"
        :expiration-date="item.expiration_date"
        :expired="item.expired"
        :suspended="item.suspended"
        :card-type="cardType"
        :target="linkTarget"
        :minisite-link="!child ? item.minisite_url : null"
        :show-extra-info="extraInfoOnCard"
        :time-id="String(item.time_id)"
        :catalog-link="item.catalog_url"
        :show-catalog-link="showCatalogLink"
        :show-modal-link="showModalLink"
        :activation-details="!compactLayout"
        :show-bookmark="showBookmark"
        :bookmark="getBookmarks(item)[0]"
        :volumes="volumes(item)"
        :link-color="linkColor"
        :is-evaluation="item.is_evaluation"
        :demo="item.demo"
        @cover-click="coverClicked(item)"
        @open-info="openActivationsModal(item)"
        @open-minisite="!child ? openMinisite(item) : openChildrenBook($event, item)"
        @toggle-bookmark="toggleBookmark(item)"
      />
    </div>
    <div :class="columns" class="bookslist__slot">
      <slot name="default"></slot>
    </div>
    <ActivationsModal
      v-if="modalVisible"
      :child-name="childName"
      :opera-time-book="bookSelected"
      @close-modal="modalVisible = false"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import CardProduct from "@/components/patterns/ProductCard";
import InputSelect from "@/components/elements/InputSelect";
import InputText from "@/components/elements/InputText";
import bookmarksSetup from "@/components/mixins/bookmarksSetup";
import ActivationsModal from "~/components/books/components/ActivationsModal";

export default {
  name: "ActivatedBooksList",
  components: { CardProduct, InputSelect, InputText, ActivationsModal },
  mixins: [bookmarksSetup],
  props: {
    agent: {
      type: Boolean,
      default: false,
    },
    student: {
      type: Boolean,
      default: false,
    },
    asyncConf: {
      type: Object,
      default: () => {},
    },
    compactLayout: {
      type: Boolean,
      default: false,
    },
    cardType: {
      type: String,
      default: "normal",
    },
    child: {
      type: String,
      default: null,
    },
    childName: {
      type: String,
      default: null,
    },
    columnsClass: {
      type: String,
      default: "",
    },
    extraInfoOnCard: {
      type: Boolean,
      default: true,
    },
    linkColor: {
      type: String,
      default: "",
    },
    linkTarget: {
      type: String,
      default: "",
    },
    listColumns: {
      type: Number,
      default: 12,
    },
    colSize: {
      type: Number,
      default: 3,
    },
    search: {
      type: Boolean,
      default: false,
    },
    showCatalogLink: {
      type: Boolean,
      default: false,
    },
    books: {
      type: Array,
      required: true,
    },
    showBookmark: {
      type: Boolean,
      default: true,
    },
    showModalLink: {
      type: Boolean,
      default: true,
    },
    showVolumesLabel: {
      type: Boolean,
      default: true,
    },
    sliceSize: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      searchText: "",
      filteredBooks: this.books,
      bookSelected: {},
      columns: this.getColumnNumber(),
      modalVisible: false,
      selectData: [
        { value: "important_asc", label: "importanti" },
        { value: "title_asc", label: "titolo (a-z)" },
        { value: "title_desc", label: "titolo (z-a)" },
        { value: "activation_desc", label: "data attivazione (dai più recenti)" },
        { value: "activation_asc", label: "data attivazione (dai primi attivati)" },
      ],
      sortValue: "",
      bookmarks: {},
      bookmarksSave: "saveBook",
      bookmarkType: "book",
    };
  },
  computed: {
    ...mapGetters(["bookObjects"]),
    token() {
      return this.$cookies.get("deatoken");
    },
    /**
     * Unpack this.filteredBooks to expand the ebooks for students.
     *
     * If student flag is set, we return the list of ebooks included in the book activations, instead of the book
     * itself. Each ebook object is patched with some book data to provide consistent data:
     *
     * - cover: ebook cover
     * - time_id: parent book time_id
     * - catalog_url: parent book catalog_url
     * - parentBook: parent book object
     *
     * If sliceSize prop, the returned list is sorted by important flag and sliced before return.
     */
    filteredListItems() {
      let books = [];
      if (this.student) {
        const ebooks = {};
        this.filteredBooks.forEach(book => {
          book.activations.forEach(activation => {
            if (activation.ebooks && activation.ebooks.length) {
              activation.ebooks.forEach(ebook => {
                const patchedEbook = { ...ebook };
                patchedEbook.cover = ebook.cover_medium;
                patchedEbook.time_id = book.time_id;
                patchedEbook.catalog_url = book.catalog_url;
                patchedEbook.minisite_url = book.minisite_url;
                patchedEbook.parentBook = book;
                if (book.badgeType) {
                  patchedEbook.badgeType = book.badgeType;
                  patchedEbook.notifications = book.notifications;
                }
                ebooks[ebook.bsmart_id] = patchedEbook;
              });
            } else {
              const patchedBook = { ...book };
              patchedBook.parentBook = book;
              patchedBook.bsmart_id = `id-${book.time_id}`;
              if (book.badgeType) {
                patchedBook.badgeType = book.badgeType;
                patchedBook.notifications = book.notifications;
              }
              ebooks[book.time_id] = patchedBook;
            }
          });
        });
        books = Object.values(ebooks);
      } else books = [].concat(this.filteredBooks);
      if (this.sliceSize) {
        books = books.sort((a, b) => {
          return this.sortFunction(a, b, "important_asc");
        });
        return books.slice(0, this.sliceSize);
      }

      books = books.sort((a, b) => {
        return this.sortFunction(a, b, this.sortValue);
      });
      return books;
    },
  },
  watch: {
    books() {
      this.filteredBooks = this.books;
    },
    listColumns() {
      this.columns = this.getColumnNumber();
    },
    searchText() {
      if (this.searchText) {
        this.filteredBooks = this.books.filter(x => {
          return (
            x.title.toLowerCase().indexOf(this.searchText.toLowerCase()) > -1 ||
            x.subjects_list.toLowerCase().indexOf(this.searchText.toLowerCase()) > -1
          );
        });
      } else {
        this.filteredBooks = this.books;
      }
    },
    /**
     * Set the local bookmark mirror when the bookmark state changes.
     */
    bookObjects() {
      this.loadBookmarks();
    },
    /**
     * Store the value in the session storage to remember last sort in the future.
     *
     * When this is changed, filteredListItems is triggered automatically by the vue reactivity, so there is no need
     * to manually trigger it.
     */
    sortValue(value) {
      sessionStorage.setItem("sortValue", value);
    },
  },
  mounted() {
    if (sessionStorage.getItem("sortValue")) {
      this.sortValue = sessionStorage.getItem("sortValue");
    }
  },
  methods: {
    ...mapActions(["markAsRead"]),
    async coverClicked(item) {
      if (item.notifications && item.notifications.length > 0) {
        const token = this.$cookies.get(this.$root.config.api.tokenName);
        // https://css-tricks.com/why-using-reduce-to-sequentially-resolve-promises-works/
        await item.notifications.reduce(async (previousPromise, messageId) => {
          await previousPromise;
          return this.markAsRead({ token, messageId });
        }, Promise.resolve());
        this.$emit("update-books-list");
      }
    },
    getColumnNumber() {
      return this.columnsClass || `xs-6 s-6 m-${Math.ceil((12 / this.listColumns) * this.colSize)}`;
    },
    /**
     * Set the local bookmarks
     *
     * A filteredBooks update is triggered to force an update of filteredListItems to sort according to the new
     * bookmark state.
     */
    loadBookmarks() {
      this.bookObjects.forEach(item => {
        this.$set(this.bookmarks, item.data_obj_id, item);
      });
      this.filteredBooks = [].concat(this.filteredBooks);
    },
    openChildrenBook(event, book) {
      if (this.child) {
        if (event) event.preventDefault();
        this.$emit("open-child-book", this.child, book);
      }
    },
    openMinisite(item) {
      window.open(item.minisite_url);
    },
    /**
     * Open the modal with the activations information.
     *
     * If item is a book object, it's passed to the modal unaltered, if it's an ebook, its parentBook attribute is
     * passed to the modal.
     */
    openActivationsModal(item) {
      if (item.ebooks && item.ebooks.length) this.bookSelected = item;
      else this.bookSelected = item.parentBook;
      this.modalVisible = true;
    },
    sortFunction(term1, term2, sortValue) {
      let a;
      let b;
      let sorted;
      if (term1.book) {
        a = term1.book;
        b = term2.book;
      } else {
        a = term1;
        b = term2;
      }
      if (sortValue === "title_asc") {
        sorted = a.title.toLowerCase() >= b.title.toLowerCase();
      } else if (sortValue === "title_desc") {
        sorted = a.title.toLowerCase() < b.title.toLowerCase();
      } else if (sortValue === "activation_asc") {
        sorted = a.start_date_raw >= b.start_date_raw;
      } else if (sortValue === "activation_desc") {
        sorted = a.start_date_raw < b.start_date_raw;
      } else if (sortValue === "schoolyear_asc") {
        sorted = a.year >= b.year;
      } else if (sortValue === "schoolyear_desc") {
        sorted = a.year < b.year;
      } else if (sortValue === "subject_asc") {
        sorted = a.subjects_list.toLowerCase() >= b.subjects_list.toLowerCase();
      } else if (sortValue === "important_asc") {
        let bookmarkA;
        let bookmarkB;
        if (a.ebooks && b.ebooks) {
          // if we are comparing books, picking the first bsmart id it's enough for sorting
          bookmarkA = Object.keys(this.bookmarks).indexOf(String(a.bSmartIds[0]));
          bookmarkB = Object.keys(this.bookmarks).indexOf(String(b.bSmartIds[0]));
        } else {
          // we are comparing ebooks, so we can pick their bsmart_id attribute for bookmark checking
          bookmarkA = Object.keys(this.bookmarks).indexOf(String(a.bsmart_id));
          bookmarkB = Object.keys(this.bookmarks).indexOf(String(b.bsmart_id));
        }
        if (bookmarkA === bookmarkB) sorted = a.start_date_raw < b.start_date_raw;
        else sorted = bookmarkA < bookmarkB;
      }
      return sorted ? 1 : -1;
    },
    /**
     * Get all the bookmarks for the given book or ebook.
     *
     * @param item book or ebook to check for presence in bookmarks
     * @returns {*|null|T} bookmarks for the given book or ebook (an empty list is returned if no bookmark is found)
     */
    getBookmarks(item) {
      if (item.ebooks && item.ebooks.length) {
        const bookmarks = item.ebooks
          .map(ebook => this.bookmarks[ebook.bsmart_id])
          .filter(bookmark => (bookmark ? bookmark && bookmark.payload.time_id === item.time_id : null));
        if (bookmarks.length) return bookmarks;
        return [];
      }
      if (this.bookmarks[item.bsmart_id]) return [this.bookmarks[item.bsmart_id]];
      return [];
    },
    /**
     * Toggle bookmarks for the given book or ebook.
     *
     * To detect to add or remove bookmarks, getBookmarks is returned: if list is not empty, it means we have at
     * least one bookmark for the object and we then delete them.
     *
     * As the value returned by getBookmarks is a list we delete all the the items in that list: if item is an ebook
     * the list will contain only one bookmark for the ebook, if a book, it will contain at least one bookmark for
     * each bookmarked ebook linked to that book, in any case we process all the items in the list.
     *
     * If bookmarks list is empty, we add the bookmarks: for ebooks we add the ebook itself, for books, we add a
     * bookmark for each ebook included in that book.
     *
     * @param item book or ebook object
     */
    toggleBookmark(item) {
      const bookmarks = this.getBookmarks(item);
      if (bookmarks.length) {
        bookmarks.forEach(bookmark => {
          this.deleteBookmark(this.bookmarks[String(bookmark.data_obj_id)]);
        });
      } else if (item.ebooks && item.ebooks.length) {
        // item is a book, adding a bookmark for each included ebook
        item.ebooks.forEach(ebook => {
          this.saveBookmark(ebook);
        });
      } else {
        // item is an ebook, adding a bookmark for itself only
        this.saveBookmark(item);
      }
    },
    volumes(item) {
      if (this.showVolumesLabel && item.bSmartIds) return item.bSmartIds.length ? item.bSmartIds.length : 0;
      return null;
    },
  },
};
</script>
<style lang="scss">
@import "@/styles/system.utils";

.bookslist {
  &__filters {
    @include media("<=sm") {
      align-items: stretch;
      flex-wrap: wrap;
      justify-content: stretch;
    }

    align-items: flex-start;
    display: flex;
    justify-content: flex-end;
    padding-bottom: $space-large;

    .dea-input {
      @include media("<=sm") {
        width: 100%;
      }
    }

    .dea-input--text {
      @include media("<=sm") {
        margin-right: 0;
      }
      margin-right: $space-small;

      input {
        background: $dea-color-grey-light;
        border: 0;
        color: $dea-color-grey-dark;
      }
    }

    .dea-input--input-select {
      .dea-input__labels,
      .dea-input__support-message {
        display: none;
      }

      .dea-input__wrapper select {
        z-index: 0;
      }
    }
  }

  &__slot {
    @include media("<=m") {
      margin-bottom: $space-small;
      order: -1;
      width: 100% !important; //sass-lint:disable-line no-important
    }
  }

  &__column {
    &--mybooks {
      @include media("<=l") {
        width: 20%;
      }
      @include media("<=m") {
        width: 25%;
      }
      @include media("<=s") {
        width: 33%;
      }
      @include media("<=sm") {
        width: 50%;
      }
      @include media("<=xs") {
        width: 100%;
      }
      padding: 0 $gutter-half;
      width: 16.66%;
    }

    &--myhome {
      @include media("<=m") {
        width: 20%;
      }
      @include media("<=s") {
        width: 33%;
      }
      @include media("<=sm") {
        width: 50%;
      }
      @include media("<=xs") {
        width: 100%;
      }
      padding: 0 $gutter-half;
      width: 16.66%;
    }

    &--agents {
      @include media("<=m") {
        flex: 1 0 calc(20% - #{$space-small});
        max-width: calc(20% - #{$space-small});
      }

      @include media("<900px") {
        flex: 1 0 calc(25% - #{$space-small});
        max-width: calc(25% - #{$space-small});
      }

      @include media("<700px") {
        flex: 1 0 calc(33% - #{$space-small});
        max-width: calc(33% - #{$space-small});
      }

      @include media("<500px") {
        flex: 1 0 calc(50% - #{$space-small});
        max-width: calc(50% - #{$space-small});
      }

      flex: 1 0 calc(20% - #{$space-small});
      margin: $space-x-small;
      max-width: calc(20% - #{$space-small});

      .dea-product-card {
        margin: 0 auto;
      }
    }
  }
}
</style>
